/**
 * Ordinal build/runtime gate level for a route, shared by V2 and V3.
 */
export const RouteLevels = {
  /** Off in V3, and V2 keeps its page. Compiled, it makes a dormant route that can be raised at runtime; at runtime any route can be lowered to it. */
  Disabled: 0,
  /** Route exists in V3, but V2 keeps serving its own page (no handoff). */
  V3: 1,
  /** V3 owns it and V2 redirects — still gated at runtime by feature flags and other conditions. */
  V2Redirect: 2,
  /** V3-only: V2 always hands off, bypassing all contextual checks. */
  V3Only: 3,
} as const;

export type RouteLevel = (typeof RouteLevels)[keyof typeof RouteLevels];

/** Whether `value` is one of the {@link RouteLevels}. */
export function isRouteLevel(value: unknown): value is RouteLevel {
  return (Object.values(RouteLevels) as unknown[]).includes(value);
}

export type RouteKey =
  | 'login'
  | 'demo'
  | 'bulkTags'
  | 'accountSettings'
  | 'urlParameters'
  | 'userGroups'
  | 'webhooks'
  | 'authentications'
  | 'pixels'
  | 'mfa'
  | 'apiTokens'
  | 'loginSecurity'
  | 'rateLimits'
  | 'recordings'
  | 'predictiveRouting'
  | 'tcpaShield'
  | 'platforms'
  | 'terms'
  | 'numbers'
  | 'numberPools'
  | 'blockedNumbers'
  | 'numberPoolSummary'
  | 'targetDialInsights'
  | 'outboundBidRequests'
  | 'ringTreeAdvancedSettings'
  | 'outboundPingInsights'
  | 'rtbBillingInsights'
  | 'rtbSummary'
  | 'dashboard'
  | 'transcription'
  | 'users'
  | 'billingSettings'
  | 'userProfile'
  | 'accountDetails'
  | 'publisherRegistration'
  | 'buyerRegistration';

/**
 * Route levels for V3, keyed by route name.
 * - Holds every route the build includes.
 * - A route the build leaves out has no entry.
 *
 * WARNING: Avoid renaming existing keys without proper search & replace across the codebase!
 * V2 code isn't type safe and relies on string literals for route flag names.
 */
export type Routes = Partial<Record<RouteKey, RouteLevel>>;

/** A level for every route, `null` where a build leaves the route out. */
export type RouteTable = Record<RouteKey, RouteLevel | null>;
